import { Box, Container, Grid2 } from "@mui/material";
import { useRef } from "react";
import { Link } from "react-router-dom";

export const HighSeverityPotentialClaimsDetail = () => {
  const productSectionRef = useRef(null);
  const valueSectionRef = useRef(null);
  const financialSectionRef = useRef(null);

  const scrollToSection = (sectionRef) => {
    if (!sectionRef?.current) return;
    const sectionTop =
      sectionRef.current.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: sectionTop - 110, behavior: "smooth" });
  };

  return (
  <Container maxWidth="lg" className="pt-6">
      <Box className="mb-8 md:mb-10 rounded-xl border border-[#021744] bg-[#021744]">
        <Box className="bg-[#021744] text-white rounded-xl p-6 md:p-10">
          <Box className="w-full flex flex-wrap justify-between items-start gap-4 mb-6">
            <span className="inline-flex text-xs px-3 py-1 rounded-full bg-[#42DDD1] border border-[#42DDD1] text-[#021744]">
              Use Case Spotlight
            </span>
            <Box className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => scrollToSection(valueSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Value
              </button>
              <button
                type="button"
                onClick={() => scrollToSection(financialSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Financial Impact
              </button>
              <button
                type="button"
                onClick={() => scrollToSection(productSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Product
              </button>
            </Box>
          </Box>
          <h1 className="text-left text-[26px] md:text-[34px] tracking-normal leading-10 font-medium mb-4">
            Catch High-Severity Potential Early — With Context You Can Act On
          </h1>
          <p className="text-[#d6e6ff] leading-7 mb-4 text-left">
            Bodily injury claims can escalate quickly—what starts as a routine
            case can become high severity within hours. Early attorney
            involvement, unexpected treatment progression, and prolonged
            recovery timelines can drive inflated reserves, higher payouts, and
            extended claim cycle times. For insurers, early identification of
            severity risk is critical to controlling outcomes and improving
            claimant experience.
          </p>
          <p className="text-[#d6e6ff] leading-7 text-left">
            4SeeCharlee brings AI-driven severity assessment to the frontlines.
            It continuously monitors structured data (e.g., injury codes,
            treatment types) and unstructured inputs (adjuster notes, medical
            records, demand packages) to detect signs of high-severity potential
            early. More than just a score, Charlee provides contextual
            insights—flagging why a claim is escalating and helping teams
            prioritize triage, allocate resources, and avoid costly delays.
            These severity-scored claims integrate seamlessly into existing
            workflows via APIs and Charlee AI Agents, with no disruption to
            claims operations.
          </p>
        </Box>
      </Box>
      <Grid2 container spacing={2} className="mb-8 items-stretch">
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">Role</p>
            <p className="text-sm text-[#1a2f55]">
              Senior Claims Adjuster, Supervisor, Manager
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">
              Job at Hand
            </p>
            <p className="text-sm text-[#1a2f55]">
              Prioritize high-risk cases early for better handling
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">Trigger</p>
            <p className="text-sm text-[#1a2f55]">
              Spike in bodily injury claims following accident surge or
              catastrophic event
            </p>
          </Box>
        </Grid2>
      </Grid2>
      <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4">
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Opportunity
        </h3>
        <p className="text-[#42506a]">
          Early identification of severe claims improves outcomes, reduces
          costs, and enhances claimant satisfaction.
        </p>
      </Box>
      <Grid2
        ref={productSectionRef}
        container
        spacing={2}
        className="mb-4 items-stretch"
      >
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 h-full">
            <h4 className="text-[#021744] font-medium mb-2">Before Charlee</h4>
            <p className="text-[#42506a]">
              Severity identification relied on adjuster judgment, inconsistent
              documentation, and subjective triage processes.
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 h-full">
            <h4 className="text-[#021744] font-medium mb-2">With Charlee</h4>
            <p className="text-[#42506a]">
              AI continuously analyzes medical indicators, claim narratives, and
              historical risk patterns to flag high-severity potential cases and
              surface them for early intervention.
            </p>
          </Box>
        </Grid2>
      </Grid2>
      <Grid2 container spacing={2} className="mb-4 items-stretch">
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">Product Used</p>
            <p>🔎 4SeeCharlee</p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">LOBs</p>
            <p>All</p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">Area</p>
            <Box className="flex gap-2">
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                Claims
              </span>
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                Severity
              </span>
            </Box>
          </Box>
        </Grid2>
      </Grid2>
      <Box
        ref={valueSectionRef}
        className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4"
      >
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Value Proposition
        </h3>
        <p className="text-[#42506a] text-left">
          Severity-scored claims integrate into existing workflows via APIs and
          AI agents, improving triage and speed to action.
        </p>
      </Box>
      <Box
        ref={financialSectionRef}
        className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4"
      >
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Financial Impact
        </h3>
        <p className="text-[#42506a] mb-2 text-left">
          Delayed identification of high-severity claims often results in:
        </p>
        <ul className="text-[#42506a] space-y-2 mb-3 text-left">
          <li>
            • Inflated settlements due to late medical management or missed
            negotiation windows
          </li>
          <li>
            • Higher legal costs, as delayed intervention increases the
            likelihood of attorney involvement
          </li>
          <li>
            • Claimant dissatisfaction, leading to complaints and reputational
            risk
          </li>
        </ul>
        <Box className="rounded-lg border border-[#E6EAF2] bg-[#EDF3F8] p-4 text-left">
          <p className="text-[#021744] font-bold mb-2">
            Charlee delivers measurable financial value:
          </p>
          <ul className="text-[#42506a] space-y-2 text-left">
            <li>
              • $7K–$18K estimated savings per high-severity claim through early
              triage and avoidance of escalation
            </li>
            <li>
              • Reduction in unnecessary specialist assignments and
              over-reserving
            </li>
            <li>
              • Shorter claim durations and faster resolutions, boosting
              operational efficiency and satisfaction
            </li>
            <li>
              • Reduced late-stage litigation, reserve leakage, and customer
              dissatisfaction
            </li>
          </ul>
        </Box>
      </Box>
      <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4">
        <h3 className="text-[28px] md:text-[32px] text-[#021744] font-bold mb-4">
          Results
        </h3>
        <Grid2 container spacing={2} className="items-stretch">
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p className="font-semibold text-[#021744] whitespace-nowrap">
                🚀 60%
              </p>
              <p className="text-[#42506a]">
                faster escalation to specialist teams
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p className="text-xl md:text-2xl font-bold text-[#021744] whitespace-nowrap">
                ✅ +3
              </p>
              <p className="text-[#42506a]">
                point improvement in claimant satisfaction scores
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p>💬</p>
              <p className="text-[#42506a]">
                “It’s like having another set of eyes reviewing every claim.”
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p className="text-xl md:text-2xl font-bold text-[#021744] whitespace-nowrap">
                ⏱️ &lt; 30 days
              </p>
              <p className="text-[#42506a]">Deployed; rapid time-to-value</p>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4">
        <Box className="rounded-lg border border-[#E6EAF2] bg-[#EDF3F8] p-4 text-left">
          <p className="text-[#021744] font-bold mb-2">Why It Matters</p>
          <p className="text-[#42506a]">
            Early visibility into high-severity potential improves triage,
            optimizes resource use, reduces payout volatility, and protects
            customer relationships.
          </p>
        </Box>
      </Box>
      <Box className="pt-8 text-center">
        <Link
          to="/blogs/usecases"
          className="inline-flex items-center gap-2 text-[#021744] border border-[#021744] bg-white rounded-full px-6 py-3 hover:bg-[#021744] hover:text-white"
        >
          Back to Use Cases
        </Link>
      </Box>
    </Container>
  );
};
